<template>
    <applicants-find
        v-slot="{
            has_loaded,
            is_loading,
            data: applicant,
            error: find_error,
            find
        }"
        :params="{
            applicant: $route.params.applicant
        }"
        :include="[
            'employers',
            'landlord'
        ]"
        @success="onFindSuccess"
    >
        <div>
            <template v-if="!has_loaded || find_error">
                <base-loading
                    :error="find_error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="find_error"
                    :error="find_error"
                />
            </template>

            <template v-else>
                <applicants-employers-update
                    v-slot="{
                        sid: employers_sid,
                        promise: employersPromise
                    }"
                    :params="{
                        applicant: applicant.uuid
                    }"
                >
                    <applicants-landlords-store
                        v-slot="{
                            sid: landlord_sid,
                            promise: landlordPromise
                        }"
                        :params="{
                            applicant: applicant.uuid
                        }"
                    >
                        <promise-all
                            v-slot="{
                                is_loading: is_saving,
                                errors: execute_errors,
                                execute
                            }"
                            :execute-on-created="false"
                            :callstack="{
                                employers: () => employersPromise(forms.employers),
                                landlord: () => landlordPromise(forms.landlord)
                            }"
                            @success="onExecuteSuccess"
                            @error="onExecuteError"
                        >
                            <base-form @submit="execute">
                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">Employment</template>
                                        <template slot="description">
                                            At least <strong>two years</strong> of employment history is reccomended.
                                        </template>

                                        <div
                                            v-for="(employer, index) in forms.employers"
                                            :key="index"
                                            class="relative mt-3 mb-6 border rounded"
                                        >
                                            <header class="flex items-center px-4 py-3 border-b bg-grey-lightest">
                                                <div class="flex-grow">
                                                    {{ index + 1 | ordinal }} Employer
                                                </div>

                                                <button
                                                    type="button"
                                                    class="flex-none w-6 h-6 focus:outline-none"
                                                    @click.prevent="removeEmployer(employer, index)"
                                                >
                                                    <base-svg
                                                        name="icon-trash"
                                                        class="w-full h-full"
                                                    />
                                                </button>
                                            </header>

                                            <div class="px-4 pt-6 border-b">
                                                <grid-layout>
                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][name]`"
                                                            :validator-name="`data.${index}.name`"
                                                        >
                                                            <template slot="label">Employer's Name</template>

                                                            <input-text
                                                                v-model="forms.employers[index].name"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][name]`"
                                                                :validator-name="`data.${index}.name`"
                                                                placeholder="Rentora Ltd."
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][job_title]`"
                                                            :validator-name="`data.${index}.job_title`"
                                                        >
                                                            <template slot="label">Your job title/position</template>

                                                            <input-text
                                                                v-model="forms.employers[index].job_title"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][job_title]`"
                                                                :validator-name="`data.${index}.job_title`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][started_at]`"
                                                            :validator-name="`data.${index}.started_at`"
                                                        >
                                                            <template slot="label">Started At</template>

                                                            <input-date
                                                                v-model="forms.employers[index].started_at"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][started_at]`"
                                                                :validator-name="`data.${index}.started_at`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :name="`employers[${index}][finished_at]`"
                                                            :validator-name="`data.${index}.finished_at`"
                                                        >
                                                            <template slot="label">Finished At</template>

                                                            <input-date
                                                                v-model="forms.employers[index].finished_at"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][finished_at]`"
                                                                :validator-name="`data.${index}.finished_at`"
                                                            />

                                                            <template slot="hint">Leave blank if you're still employed here.</template>
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][salary]`"
                                                            :validator-name="`data.${index}.salary`"
                                                        >
                                                            <template slot="label">Monthly take-home</template>

                                                            <div class="relative">
                                                                <input-text
                                                                    v-model="forms.employers[index].salary"
                                                                    :sid="employers_sid"
                                                                    :disabled="is_saving"
                                                                    :name="`employers[${index}][salary]`"
                                                                    :validator-name="`data.${index}.salary`"
                                                                    class="pl-7"
                                                                />

                                                                <div class="absolute inset-y-0 left-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                                                    &pound;
                                                                </div>
                                                            </div>
                                                        </form-item>
                                                    </grid-layout-item>
                                                </grid-layout>
                                            </div>

                                            <div class="px-4 pt-6">
                                                <h6 class="mb-3 text-base font-normal">Contact</h6>

                                                <grid-layout>
                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][first_name]`"
                                                            :validator-name="`data.${index}.first_name`"
                                                        >
                                                            <template slot="label">First Name</template>

                                                            <input-text
                                                                v-model="forms.employers[index].first_name"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][first_name]`"
                                                                :validator-name="`data.${index}.first_name`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :is-required="true"
                                                            :name="`employers[${index}][last_name]`"
                                                            :validator-name="`data.${index}.last_name`"
                                                        >
                                                            <template slot="label">Last Name</template>

                                                            <input-text
                                                                v-model="forms.employers[index].last_name"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][last_name]`"
                                                                :validator-name="`data.${index}.last_name`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :name="`employers[${index}][email]`"
                                                            :validator-name="`data.${index}.email`"
                                                            :is-required="true"
                                                            required-text="†"
                                                        >
                                                            <template slot="label">Email</template>

                                                            <input-text
                                                                v-model="forms.employers[index].email"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][email]`"
                                                                :validator-name="`data.${index}.email`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="employers_sid"
                                                            :name="`employers[${index}][telephone]`"
                                                            :validator-name="`data.${index}.telephone`"
                                                            :is-required="true"
                                                            required-text="†"
                                                        >
                                                            <template slot="label">Telephone</template>

                                                            <input-text
                                                                v-model="forms.employers[index].telephone"
                                                                :sid="employers_sid"
                                                                :disabled="is_saving"
                                                                :name="`employers[${index}][telephone]`"
                                                                :validator-name="`data.${index}.telephone`"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>
                                                </grid-layout>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            class="btn btn-blue-lightest"
                                            @click.prevent="addEmployer()"
                                        >
                                            <template v-if="forms.employers.length == 0">
                                                Add an employer
                                            </template>
                                            <template v-else>
                                                Add a {{ forms.employers.length + 1 | ordinal }} employer
                                            </template>
                                        </button>
                                    </form-section>
                                </div>

                                <div class="px-4 py-6 border-b sm:px-6">
                                    <form-section>
                                        <template slot="title">Landlord</template>
                                        <template slot="description">
                                            Contact information for your most recent landlord
                                        </template>

                                        <grid-layout>
                                            <grid-layout-item class="md:w-1/2">
                                                <form-item
                                                    :sid="landlord_sid"
                                                    name="first_name"
                                                >
                                                    <template slot="label">First Name</template>

                                                    <input-text
                                                        v-model="forms.landlord.first_name"
                                                        :sid="landlord_sid"
                                                        :disabled="is_saving"
                                                        name="first_name"
                                                    />
                                                </form-item>
                                            </grid-layout-item>

                                            <grid-layout-item class="md:w-1/2">
                                                <form-item
                                                    :sid="landlord_sid"
                                                    name="last_name"
                                                >
                                                    <template slot="label">Last Name</template>

                                                    <input-text
                                                        v-model="forms.landlord.last_name"
                                                        :sid="landlord_sid"
                                                        :disabled="is_saving"
                                                        name="last_name"
                                                    />
                                                </form-item>
                                            </grid-layout-item>

                                            <grid-layout-item class="md:w-1/2">
                                                <form-item
                                                    :sid="landlord_sid"
                                                    name="email"
                                                >
                                                    <template slot="label">Email</template>

                                                    <input-text
                                                        v-model="forms.landlord.email"
                                                        :sid="landlord_sid"
                                                        :disabled="is_saving"
                                                        name="email"
                                                    />
                                                </form-item>
                                            </grid-layout-item>

                                            <grid-layout-item class="md:w-1/2">
                                                <form-item
                                                    :sid="landlord_sid"
                                                    name="telephone"
                                                >
                                                    <template slot="label">Telephone</template>

                                                    <input-text
                                                        v-model="forms.landlord.telephone"
                                                        :sid="landlord_sid"
                                                        :disabled="is_saving"
                                                        name="telephone"
                                                    />
                                                </form-item>
                                            </grid-layout-item>
                                        </grid-layout>
                                    </form-section>
                                </div>

                                <base-form-footer class="rounded-b">
                                    <base-button
                                        v-slot:default
                                        :is-loading="is_saving"
                                        class="w-full btn-blue md:w-auto"
                                    >
                                        Continue
                                    </base-button>

                                    <template v-slot:required>
                                        <span class="text-red">•</span> Required<br>
                                        <span class="text-red">†</span> One required
                                    </template>
                                </base-form-footer>

                                <div
                                    v-for="(execute_error, index) in execute_errors"
                                    :key="index"
                                >
                                    <error-modal
                                        v-if="execute_error && $error.is(execute_error, { exclude: [400, 422] })"
                                        :error="execute_error"
                                    />
                                </div>
                            </base-form>
                        </promise-all>
                    </applicants-landlords-store>
                </applicants-employers-update>
            </template>
        </div>
    </applicants-find>
</template>

<script>
    import _ from 'lodash'
    import scroll from '../../../../support/scroll'
    import moment from 'moment'
    import HeadMixin from '../../../../mixins/head'
    import PreloadedMixin from '../../../../mixins/preloaded'
    import ApplicantsEmployersUpdate from '../../../../components/applicants-employers-update'
    import ApplicantsFind from '../../../../components/applicants-find'
    import ApplicantsLandlordsStore from '../../../../components/applicants-landlords-store'
    import PromiseAll from '../../../../components/promise-all'

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            ApplicantsEmployersUpdate,
            ApplicantsFind,
            ApplicantsLandlordsStore,
            PromiseAll
        },
        data () {
            return {
                forms: {
                    employers: [],
                    landlord: {
                        first_name: '',
                        last_name: '',
                        email: '',
                        telephone: ''
                    }
                }
            }
        },
        methods: {
            head () {
                const address = _.get(this.preloaded('application'), 'data.listing.property.address.name')

                return {
                    title: [
                        'References',
                        `${address} Application`
                    ]
                }
            },
            onFindSuccess ({ data: { landlord, employers: { data: employers } } }) {
                if (employers.length) {
                    _.each(employers, employer => {
                        this.addEmployer({
                            uuid: employer.uuid,
                            name: employer.name,
                            job_title: employer.job_title,
                            salary: employer.salary / 100,
                            started_at: moment(employer.started_at).format('DD/MM/YYYY'),
                            finished_at: employer.finished_at
                                ? moment(employer.started_at).format('DD/MM/YYYY')
                                : '',
                            first_name: employer.first_name,
                            last_name: employer.last_name,
                            email: employer.email,
                            telephone: employer.telephone
                        })
                    })
                } else {
                    this.addEmployer()
                }

                if (landlord) {
                    this.forms.landlord.first_name = landlord.first_name
                    this.forms.landlord.last_name = landlord.last_name
                    this.forms.landlord.email = landlord.email
                    this.forms.landlord.telephone = landlord.telephone
                }
            },
            onExecuteSuccess () {
                this.$router.push({
                    name: 'applications.applicants.confirm.create'
                })
            },
            onExecuteError () {
                scroll.toTop()
            },
            addEmployer (data = {}) {
                this.forms.employers.push(_.extend({
                    name: '',
                    job_title: '',
                    started_at: '',
                    finished_at: '',
                    salary: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    telephone: ''
                }, data))
            },
            removeEmployer (employer, index) {
                this.forms.employers = _.remove(this.forms.employers, (employer, j) => {
                    return index !== j
                })

                this.$validator.reset()
            },
            isTelephone (value) {
                return !value.includes('@')
            }
        }
    }
</script>
